/**
 * MULGOKI Redesign — Main JavaScript
 * Premium K-pop Trading Card Store
 * ─────────────────────────────────────────────
 * Features:
 *   1. Scroll Reveal (IntersectionObserver)
 *   2. Header Scroll Behavior
 *   3. Card Tilt Effect (3D hover)
 *   4. Countdown Timer
 *   5. Smooth Anchor Scroll
 */

document.addEventListener('DOMContentLoaded', () => {

  /* ───────────────────────────────────────────
   * 1. SCROLL REVEAL SYSTEM
   * ─────────────────────────────────────────── */
  try {
    const revealElements = document.querySelectorAll('.reveal');
    if (revealElements.length && window.IntersectionObserver) {
      const revealObserver = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;

            const el = entry.target;
            const delay = el.dataset.delay;
            if (delay) {
              el.style.transitionDelay = `${parseFloat(delay)}s`;
            }

            el.classList.add('reveal--visible');
            observer.unobserve(el);
          });
        },
        { threshold: 0.15 }
      );

      revealElements.forEach((el) => revealObserver.observe(el));
    }
  } catch (e) {
    console.error("Scroll Reveal Init Error:", e);
  }

  /* ───────────────────────────────────────────
   * 2. HEADER SCROLL BEHAVIOR
   * ─────────────────────────────────────────── */
  try {
    const header = document.querySelector('.section-header') || document.getElementById('header-group');
    if (header) {
      let lastScrolled = false;
      const onScroll = () => {
        const scrolled = window.scrollY > 100;
        if (scrolled !== lastScrolled) {
          header.classList.toggle('header--scrolled', scrolled);
          lastScrolled = scrolled;
        }
      };

      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }
  } catch (e) {
    console.error("Header Scroll Init Error:", e);
  }

  /* ───────────────────────────────────────────
   * 3. CARD TILT EFFECT (3D hover)
   * ─────────────────────────────────────────── */
  try {
    const isTouchOnly = window.matchMedia('(hover: none)').matches;
    if (!isTouchOnly) {
      const tiltCards = document.querySelectorAll('.card-tilt');
      const MAX_TILT = 5; // degrees

      tiltCards.forEach((card) => {
        card.addEventListener('mousemove', (e) => {
          const rect = card.getBoundingClientRect();
          if (rect.width === 0 || rect.height === 0) return;

          const centerX = rect.left + rect.width / 2;
          const centerY = rect.top + rect.height / 2;

          const normX = (e.clientX - centerX) / (rect.width / 2);
          const normY = (e.clientY - centerY) / (rect.height / 2);

          const rotateY = (normX * MAX_TILT).toFixed(2);
          const rotateX = (-normY * MAX_TILT).toFixed(2);

          card.style.transform = `perspective(600px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`;
          card.style.transition = 'transform 0.1s ease-out';
        });

        card.addEventListener('mouseleave', () => {
          card.style.transform = 'perspective(600px) rotateX(0deg) rotateY(0deg)';
          card.style.transition = 'transform 0.45s ease-out';
        });
      });
    }
  } catch (e) {
    console.error("Card Tilt Init Error:", e);
  }

  /* ───────────────────────────────────────────
   * 4. COUNTDOWN TIMER
   * ─────────────────────────────────────────── */
  try {
    const countdowns = document.querySelectorAll('[data-target-date]');
    countdowns.forEach((el) => {
      if (!el.dataset.targetDate) return;
      const targetDate = new Date(el.dataset.targetDate).getTime();
      if (isNaN(targetDate)) return;

      const expiredText = el.dataset.expiredText || 'AVAILABLE NOW';
      const daysEl = el.querySelector('.countdown__days');
      const hoursEl = el.querySelector('.countdown__hours');
      const minutesEl = el.querySelector('.countdown__minutes');
      const secondsEl = el.querySelector('.countdown__seconds');

      if (!daysEl || !hoursEl || !minutesEl || !secondsEl) return;

      const pad = (n) => String(n).padStart(2, '0');

      const tick = () => {
        const now = Date.now();
        const diff = targetDate - now;

        if (diff <= 0) {
          el.classList.add('countdown--expired');
          el.innerHTML = `<span class="countdown__expired-text">${expiredText}</span>`;
          return false;
        }

        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
        const minutes = Math.floor((diff / (1000 * 60)) % 60);
        const seconds = Math.floor((diff / 1000) % 60);

        daysEl.textContent = pad(days);
        hoursEl.textContent = pad(hours);
        minutesEl.textContent = pad(minutes);
        secondsEl.textContent = pad(seconds);

        return true;
      };

      if (tick()) {
        const interval = setInterval(() => {
          if (!tick()) clearInterval(interval);
        }, 1000);
      }
    });
  } catch (e) {
    console.error("Countdown Timer Init Error:", e);
  }

  /* ───────────────────────────────────────────
   * 5. SMOOTH ANCHOR SCROLL
   * ─────────────────────────────────────────── */
  try {
    const HEADER_OFFSET = 80; // px
    document.querySelectorAll('a[href^="#"]').forEach((link) => {
      link.addEventListener('click', (e) => {
        const href = link.getAttribute('href');
        if (!href || href === '#') return;

        let target;
        try {
          target = document.querySelector(href);
        } catch (selectorErr) {
          return; // Ignore invalid selectors like double hashes or colons
        }

        if (!target) return;

        e.preventDefault();
        const top = target.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;

        window.scrollTo({
          top,
          behavior: 'smooth',
        });

        history.pushState(null, '', href);
      });
    });
  } catch (e) {
    console.error("Smooth Anchor Scroll Init Error:", e);
  }

}); // end DOMContentLoaded
